import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TableSkeleton from "shared/components/TableSkeleton";
import {
  headings,
  columnHeadings,
  skeletonInfo,
  filterOptions,
  typeFilterOptions,
} from "./constants";
import { coupon_type_enums } from "shared/utils/constants";
import useWindowSize from "shared/customHooks/windowSize";
import styles from "./style.module.scss";
import { Icons } from "assets";
import Pagination from "shared/components/pagination";
import ItemsPerPage from "shared/components/itemsPerPage";
import NoDataFound from "shared/components/noDataFound";
import { routeConstant } from "shared/routes/routesConstant";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import useDebounce from "shared/customHooks/useDebounce";
import Filter from "shared/components/filterButton";
import CustomButton from "shared/components/customButton";
import CardComp from "shared/components/card";
import CreatePromoModal from "shared/modals/createPromoModal";
import PromoDetailsModal from "shared/modals/promoDetailsModal";
import Confirmation from "shared/modals/confirmation";
import { toastMessage } from "shared/components/toast";
import {
  getCouponsService,
  deleteCouponService,
} from "shared/services/accountantService";

function PromoCodes() {
  const screenWidth = useWindowSize();
  const [search, setSearch] = useState<string>("");
  const [activePage, setActivePage] = useState<number>(1);
  // Starts true: a fetch is fired on mount, so the first paint must be the
  // skeleton. Starting false flashes "No Data Found" before the request runs.
  const [loader, setLoader] = useState<boolean>(true);
  const [promoCodes, setPromoCodes] = useState<any>([]);
  const [totalItems, setTotalItems] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(10);
  const [searchText, setSearchText] = useState<string>("");
  const [selectedFilter, setSelectedFilter] = useState<any>(null);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<any>(null);
  const [createModal, setCreateModal] = useState<boolean>(false);
  const [editModal, setEditModal] = useState<boolean>(false);
  const [selectedPromo, setSelectedPromo] = useState<any>(null);
  const [openPromoDetailsModal, setOpenPromoDetailsModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] =
    useState<boolean>(false);
  const [deleteLoader, setDeleteLoader] = useState<boolean>(false);

  // "All" clears the filter rather than applying one, so it gets no chip
  const applyFilter = (setter: any) => (option: any) => {
    setter(
      option?.value === null || option?.value === undefined ? null : option,
    );
    setActivePage(1);
  };

  useBreadcrumbs({
    title: "Promo Codes",
    path: routeConstant.promoCodes.path,
  });

  useDebounce(
    () => {
      const trimmedSearch = search.trim();
      if (trimmedSearch === searchText) return;
      setSearchText(trimmedSearch);
      setActivePage(1);
    },
    [search],
    800,
  );

  const getPromoCodes = () => {
    setLoader(true);
    getCouponsService(
      searchText,
      activePage,
      itemsPerPage,
      selectedFilter?.value,
      selectedTypeFilter?.value,
    )
      .then(({ data, status }) => {
        if (status) {
          const responseData = data?.data;
          setPromoCodes(responseData?.data || []);
          setTotalItems(responseData?.total || 0);
          setTotalPage(responseData?.last_page || 1);
          setCurrentPage(responseData?.current_page || 1);
          setLoader(false);
        }
      })
      .catch((err) => {
        console.log("Error-----", err);
        toastMessage("error", err?.response?.data?.message);
        setLoader(false);
      });
  };

  const handleDeletePromo = (promo: any) => {
    setDeleteLoader(true);
    deleteCouponService(promo.id)
      .then(({ data, status }) => {
        setDeleteLoader(false);
        if (status) {
          getPromoCodes();
          toastMessage(
            "success",
            data?.message || "Coupon deleted successfully",
          );
          setOpenConfirmationModal(false);
        }
      })
      .catch((err: any) => {
        console.log("Error----", err);
        toastMessage("error", err?.response?.data?.message);
        setDeleteLoader(false);
        setOpenConfirmationModal(false);
      });
  };

  useEffect(() => {
    getPromoCodes();
  }, [
    selectedFilter,
    selectedTypeFilter,
    searchText,
    itemsPerPage,
    activePage,
  ]);

  return (
    <section>
      <Box className={styles.topLevelContainer}>
        <Box className={styles.topHeadContainer}>
          <Box>
            <div className={styles.tableHeading}>{headings.mainHeading}</div>
            <div className={styles.tableSubHeading}>{headings.subHeading}</div>
          </Box>
          <Box className={styles.filterContainer}>
            <div className={styles.gridContainer}>
              <Icons.SearchIcon className={styles.searchIcon} />
              <input
                placeholder="Search By Promo Code"
                className={styles.input}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className={styles.filterBox}>
              <Filter
                label="Status"
                filterOption={filterOptions}
                filterStyles={styles.filterStyles}
                selectedOption={selectedFilter}
                setSelectedOption={applyFilter(setSelectedFilter)}
              />
            </div>
            <div className={styles.filterBox}>
              <Filter
                label="Type"
                filterOption={typeFilterOptions}
                filterStyles={styles.filterStyles}
                selectedOption={selectedTypeFilter}
                setSelectedOption={applyFilter(setSelectedTypeFilter)}
              />
            </div>
            <CustomButton
              text="Create Promo Code"
              buttonStyle={styles.createBtnSmall}
              handleClick={() => setCreateModal(true)}
            />
          </Box>
        </Box>
        <TableContainer
          className={styles.container}
          component={Paper}
          sx={
            screenWidth > 990
              ? {
                  width: `${screenWidth - 300}px`,
                }
              : {
                  boxShadow: "none",
                }
          }
        >
          <Table className={styles.table} aria-label="simple table">
            <TableHead>
              <TableRow className={styles.tableHead}>
                {columnHeadings?.map((heading, index) => {
                  return (
                    <TableCell key={heading.id}>{heading.title}</TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loader ? (
                <TableSkeleton
                  rows={skeletonInfo.rows}
                  cellCount={skeletonInfo.cellCount}
                  actionCount={skeletonInfo.actions}
                  showPersonData={false}
                ></TableSkeleton>
              ) : (
                promoCodes?.map((promo: any) => {
                  return (
                    <TableRow key={promo.id}>
                      <TableCell sx={{ minWidth: "120px" }}>
                        {promo.code}
                      </TableCell>
                      <TableCell sx={{ minWidth: "150px" }}>
                        {Number(promo.type) === coupon_type_enums.flat
                          ? `Rs. ${promo.flat_spmp} / month`
                          : `${promo.discount_percentage}%`}
                      </TableCell>
                      <TableCell sx={{ minWidth: "100px" }}>
                        {promo.used_quantity}/{promo.quantity}
                      </TableCell>
                      <TableCell sx={{ minWidth: "150px" }}>
                        {new Date(promo.created_at).toLocaleDateString()} -{" "}
                        {new Date(promo.expiry_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell sx={{ minWidth: "100px" }}>
                        {promo.is_active ? (
                          <span className={styles.active}>Active</span>
                        ) : (
                          <span className={styles.inactive}>Inactive</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "1rem",
                          }}
                        >
                          <Tooltip title="View">
                            <span
                              onClick={() => {
                                setSelectedPromo(promo);
                                setOpenPromoDetailsModal(true);
                              }}
                              className={styles.iconWrapper}
                            >
                              <RemoveRedEyeIcon />
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <button
                              className={styles.iconWrapper}
                              onClick={() => {
                                setSelectedPromo(promo);
                                setOpenConfirmationModal(true);
                              }}
                            >
                              <DeleteIcon />
                            </button>
                          </Tooltip>
                          {promo.is_active && (
                            <Tooltip title="Edit">
                              <span
                                className={styles.iconWrapper}
                                onClick={() => {
                                  setSelectedPromo(promo);
                                  setEditModal(true);
                                }}
                              >
                                <EditIcon />
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box>
          {totalItems && totalPage > 1 && promoCodes?.length ? (
            <Pagination
              totalPages={totalPage}
              activePage={activePage}
              setActivePage={setActivePage}
            />
          ) : null}
        </Box>
        <Box>
          {totalItems && promoCodes?.length > 0 ? (
            <ItemsPerPage
              setActivePage={setActivePage}
              currentPage={currentPage}
              setItemsPerPage={setItemsPerPage}
              itemsPerPage={itemsPerPage ? itemsPerPage : 10}
              totalItems={totalItems}
            />
          ) : null}
        </Box>
        <Box>{!loader && !(promoCodes?.length > 0) && <NoDataFound />}</Box>
      </Box>

      <CreatePromoModal
        viewModal={createModal}
        closeViewModal={setCreateModal}
        confirm={() => {
          getPromoCodes();
          toastMessage("success", "Promo code created successfully");
        }}
        loader={loader}
        isEdit={false}
      />

      <CreatePromoModal
        viewModal={editModal}
        closeViewModal={setEditModal}
        confirm={() => {
          setEditModal(false);
          getPromoCodes();
          toastMessage("success", "Promo code updated successfully");
        }}
        loader={loader}
        isEdit={true}
        selectedPromo={selectedPromo}
      />

      <PromoDetailsModal
        promoDetails={selectedPromo}
        isOpen={openPromoDetailsModal}
        closeModal={() => setOpenPromoDetailsModal(false)}
      />

      <Confirmation
        open={openConfirmationModal}
        handleClose={() => setOpenConfirmationModal(false)}
        text="Are you sure you want to delete this promo code?"
        handleClick={() => handleDeletePromo(selectedPromo)}
        loader={deleteLoader}
      />
    </section>
  );
}

export default PromoCodes;
